import type { CSSProperties, ReactElement } from 'react';
import type { DashboardFreshness, FreshnessColumn, FreshnessSegmentKey } from '../../api/dashboard';
import { cn } from '../../lib/utils';
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '../ui/tooltip';
import BreakdownTooltip from './BreakdownTooltip';
import { fmtDayYear, fmtEtDateTime, fmtInt } from '../../utils/format';

// Data freshness (BOT-451 Phase B, v2.1 design). Per NetSuite vendor: the share
// of items whose Price / Home Value a data source confirmed within 30 days, and
// how old the rest is. The API sends rows least fresh first; this component only
// draws them. Definitions: decisions/freshness.md.
//
// Hover labels use the app's Radix tooltip, not the native `title` attribute: the
// browser waits ~1-2 s before showing a `title` and the page cannot shorten that
// (BOT-492). Each bar has one hover card listing its buckets with item counts; the
// vendor's item total sits under its name (v2.2).

const HATCH = 'repeating-linear-gradient(45deg, #a1a1aa 0 2px, transparent 2px 6px)';

const SEGMENTS: Record<FreshnessSegmentKey, { label: string; style: CSSProperties }> = {
  d0_7: { label: '0-7 days', style: { backgroundColor: '#166534' } },
  d8_14: { label: '8-14 days', style: { backgroundColor: '#22c55e' } },
  d15_30: { label: '15-30 days', style: { backgroundColor: '#86efac' } },
  d31_60: { label: '31-60 days', style: { backgroundColor: '#fde68a' } },
  d61_90: { label: '61-90 days', style: { backgroundColor: '#fbbf24' } },
  d91_180: { label: '91-180 days', style: { backgroundColor: '#f97316' } },
  d181_365: { label: '181-365 days', style: { backgroundColor: '#dc2626' } },
  over_1y: { label: 'Over 1 year', style: { backgroundColor: '#7f1d1d' } },
  // A Price bar has only no_price and a Home Value bar only no_stock_info, so one
  // legend entry covers both (v2.2).
  no_price: { label: 'No data from vendor', style: { backgroundColor: '#a1a1aa' } },
  no_stock_info: { label: 'No data from vendor', style: { backgroundColor: '#a1a1aa' } },
  never: { label: 'Never checked', style: { backgroundColor: '#e4e4e7', backgroundImage: HATCH } },
};

const LEGEND: FreshnessSegmentKey[] = [
  'd0_7', 'd8_14', 'd15_30', 'd31_60', 'd61_90', 'd91_180', 'd181_365', 'over_1y',
  'no_price', 'never',
];

const STALE_AFTER_MS = 3 * 60 * 60 * 1000;

function tone(pct: number | null): string {
  if (pct == null) return 'text-zinc-500';
  if (pct >= 70) return 'text-green-800';
  if (pct >= 40) return 'text-amber-700';
  return 'text-red-700';
}

function pctLabel(pct: number | null): string {
  if (pct == null) return '—';
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

function share(items: number, total: number): string {
  const p = (100 * items) / total;
  return `${Number.isInteger(p) ? p : p.toFixed(1)}%`;
}

function plural(n: number): string {
  return `${fmtInt(n)} ${n === 1 ? 'item' : 'items'}`;
}

export default function DataFreshness({ data, now = new Date() }: { data: DashboardFreshness | null; now?: Date }) {
  return (
    <TooltipProvider delayDuration={200}>
      <section aria-labelledby="h-fresh" className="flex flex-col rounded-lg border border-border bg-background px-6 pb-3">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border">
          <h2 id="h-fresh" className="pb-3.5 pt-4 text-xs font-semibold uppercase tracking-[0.06em] text-zinc-600">
            Data freshness
          </h2>
          <ul className="ml-auto flex flex-wrap gap-x-3.5 gap-y-1.5 pb-3.5" aria-label="Legend">
            {LEGEND.map(key => (
              <li key={key} className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
                <span className="h-2.5 w-2.5 rounded-[2px]" style={SEGMENTS[key].style} />
                {SEGMENTS[key].label}
              </li>
            ))}
          </ul>
        </div>

        {data == null ? (
          <div data-testid="freshness-skeleton" aria-busy="true" className="flex flex-col gap-3 py-4">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-12 animate-pulse rounded bg-zinc-50" />)}
          </div>
        ) : data.vendors.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-zinc-500">
            Freshness is being calculated. The first numbers appear after the next hourly run.
          </p>
        ) : (
          <>
            <div className="hidden grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] gap-x-10 border-b border-zinc-100 py-2 min-[900px]:grid">
              <span className="text-xs text-zinc-600">Vendor</span>
              <span className="text-xs font-semibold text-zinc-700">Price</span>
              <span className="text-xs font-semibold text-zinc-700">Home Value</span>
            </div>
            {data.vendors.map(v => (
              <div
                key={v.label}
                data-testid="freshness-row"
                className="grid grid-cols-1 gap-x-10 gap-y-3 border-b border-zinc-100 py-3.5 min-[900px]:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] min-[900px]:items-center"
              >
                <div className="flex flex-col gap-0.5">
                  <span data-testid="freshness-vendor" className="text-sm font-medium text-zinc-950">{v.label}</span>
                  <span data-testid="freshness-total" className="text-xs tabular-nums text-zinc-500">{plural(v.total)}</span>
                </div>
                <Column vendor={v.label} title="Price" col={v.price} />
                {v.home_value ? (
                  <Column vendor={v.label} title="Home Value" col={v.home_value} />
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-zinc-500 min-[900px]:hidden">Home Value</span>
                    <span className="text-[13px] text-zinc-500">No Home Value source</span>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {data && <Footer data={data} now={now} />}
      </section>
    </TooltipProvider>
  );
}

function Hint({ label, children }: { label: string | undefined; children: ReactElement }) {
  if (!label) return children;
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </TooltipRoot>
  );
}

function Column({ vendor, title, col }: { vendor: string; title: string; col: FreshnessColumn }) {
  const drawn = col.segments.filter(s => s.items > 0);
  const sources =
    col.by_source.length > 1
      ? col.by_source.map(s => `${s.label}: ${fmtInt(s.fresh)} fresh of ${fmtInt(s.confirmed)} confirmed`).join(' · ')
      : undefined;
  const rows = drawn.map(s => ({
    key: s.key,
    label:
      s.key === 'never' && col.history_from
        ? `Never checked (tracking since ${fmtDayYear(col.history_from)})`
        : SEGMENTS[s.key].label,
    dot: SEGMENTS[s.key].style,
    value: s.items,
  }));
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-zinc-500 min-[900px]:hidden">{title}</span>
      <div className="flex items-baseline gap-2 tabular-nums">
        <Hint label={sources}>
          <span className={cn('text-lg font-semibold', tone(col.fresh_pct))} aria-label={sources}>
            {pctLabel(col.fresh_pct)}
          </span>
        </Hint>
        <span className="text-xs text-zinc-600">fresh</span>
        {/* Neutral on purpose: the % fresh color already carries the status (v2.2). */}
        <span className="ml-auto text-xs text-zinc-600">
          {fmtInt(col.older)} older than 30 days
        </span>
      </div>
      <BreakdownTooltip title={`${vendor} · ${title}`} rows={rows} total={plural(col.total)}>
        <div className="relative cursor-default" data-testid="freshness-bar">
          <div className="flex h-[18px] overflow-hidden rounded bg-zinc-100">
            {col.total > 0 &&
              drawn.map(s => (
                <div
                  key={s.key}
                  data-testid="freshness-segment"
                  aria-label={`${SEGMENTS[s.key].label}: ${share(s.items, col.total)} (${plural(s.items)})`}
                  style={{ ...SEGMENTS[s.key].style, width: `${(100 * s.items) / col.total}%` }}
                />
              ))}
          </div>
          {/* Not a hover target: its 3 px above the bar would sit closer than GAP to the card. */}
          {col.fresh_pct != null && (
            <span
              data-testid="freshness-marker"
              aria-label="30 days"
              className="pointer-events-none absolute -bottom-[3px] -top-[3px] w-0.5 bg-zinc-950"
              style={{ left: `${col.fresh_pct}%` }}
            />
          )}
        </div>
      </BreakdownTooltip>
    </div>
  );
}

function Footer({ data, now }: { data: DashboardFreshness; now: Date }) {
  const computed = [data.price.computed_at, data.home_value.computed_at].filter((x): x is string => !!x);
  const oldest = computed.length ? computed.reduce((a, b) => (a < b ? a : b)) : null;
  const stale = oldest != null && now.getTime() - new Date(oldest).getTime() > STALE_AFTER_MS;
  const backfill = data.price.backfill;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-zinc-500">
      {oldest && <span>As of {fmtEtDateTime(oldest)} · refreshed hourly</span>}
      {!backfill.done && (
        <span className="text-amber-700">
          Loading price history{backfill.through ? ` · through ${fmtDayYear(backfill.through)}` : ''}
        </span>
      )}
      {stale && <span className="text-amber-700">Not updated for over 3 hours</span>}
    </div>
  );
}
